// client/src/components/influencer/InfluencerContractAgree.js
// 프리랜서 계약 — A안 리디자인: 상태 타임라인 + 발송/안내 카드 (상태 조회·쿨다운·발송 로직은 기존 그대로)
import React, { useEffect, useState } from 'react';

const COOLDOWN = 5 * 60 * 1000;

// 유저키별 로컬 캐시 읽기(동기) → 첫 렌더에 바로 반영
function readCached(userKey) {
	if (!userKey) return { status: 'none', lastSentAt: null, cooldownLeftMs: 0 };
	const sKey = `influencer_contract_status:${userKey}`;
	const lKey = `influencer_contract_last:${userKey}`;
	const status = localStorage.getItem(sKey) || 'none';
	const last = Number(localStorage.getItem(lKey) || 0) || null;
	const left = last ? Math.max(0, COOLDOWN - (Date.now() - last)) : 0;
	return { status, lastSentAt: last, cooldownLeftMs: left };
}

function InfluencerContractAgree() {
	// 1) 세션 캐시에서 유저키 동기 로드 → 즉시 캐시 상태로 초기 렌더
	const initialUserKey = (sessionStorage.getItem('current_user_key') || '').toLowerCase();
	const cached = readCached(initialUserKey);

	const [sending, setSending] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [modalMsg, setModalMsg] = useState('');

	// 상태: 'none' | 'sent' | 'completed'
	const [status, setStatus] = useState(cached.status);
	const [lastSentAt, setLastSentAt] = useState(cached.lastSentAt);
	const [, setCooldownLeftMs] = useState(cached.cooldownLeftMs);

	// 사용자 확인
	const [userKey, setUserKey] = useState(initialUserKey);
	const [userReady, setUserReady] = useState(!!initialUserKey); // 유저키 있으면 바로 렌더 허용

	const keysOf = (uk) => ({
		status: `influencer_contract_status:${uk}`,
		last: `influencer_contract_last:${uk}`,
	});

	const openModal = (msg) => { setModalMsg(msg); setModalOpen(true); };

	// 2) 서버에서 현재 로그인 유저 확정 + 상태 동기화(정답으로 캐시 업데이트)
	useEffect(() => {
		let ignore = false;
		(async () => {
			try {
				const me = await fetch('/api/user/me', { credentials: 'include' });
				const meJson = await me.json().catch(() => ({}));
				if (!ignore && me.ok && meJson?.ok) {
					const uk = (meJson?.user?.userId || meJson?.user?.id || '').toLowerCase();
					if (uk) {
						setUserKey(uk);
						sessionStorage.setItem('current_user_key', uk);

						const res = await fetch('/api/influencer/mybiz/working-contract/status', { credentials: 'include' });
						const data = await res.json().catch(() => ({}));
						if (res.ok && data?.ok) {
							const k = keysOf(uk);
							const s = data.status || 'none';
							const last = data.lastSentAt ? Number(data.lastSentAt) : null;
							setStatus(s);
							setLastSentAt(last);
							setCooldownLeftMs(Number(data.cooldownLeftMs || 0));
							localStorage.setItem(k.status, s);
							if (last) localStorage.setItem(k.last, String(last));
						}
					}
				}
			} finally {
				if (!ignore) setUserReady(true);
			}
		})();
		return () => { ignore = true; };
	}, []);

	const handleSend = async () => {
		if (sending) return;
		if (!userReady || !userKey) { openModal('세션 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.'); return; }

		const k = keysOf(userKey);
		const now = Date.now();
		const left = lastSentAt ? Math.max(0, COOLDOWN - (now - lastSentAt)) : 0;

		// 3) 5분 이내 재요청 차단
		if (left > 0) {
			openModal('이미 메일로 계약서가 발송되었습니다. 계약서가 오지 않았다면, 회원가입 시 등록한 이메일을 확인해보시고, 5분 뒤 다시 시도해보세요.');
			return;
		}

		// 4) 최초 클릭(status=none) 또는 재발송(쿨다운 경과)
		openModal('계약서가 전송처리되었습니다. 메일이 오지 않았다면, 잠시 후 다시 시도해보세요.');
		setSending(true);
		try {
			const res = await fetch('/api/influencer/mybiz/working-contract/send', { method: 'POST', credentials: 'include' });
			const data = await res.json().catch(() => ({}));
			if (res.ok && data?.ok) {
				if (status === 'none') setStatus('sent'); // 최초만 화면 문구 갱신
				const t = Date.now();
				setLastSentAt(t);
				setCooldownLeftMs(COOLDOWN);
				localStorage.setItem(k.status, 'sent');
				localStorage.setItem(k.last, String(t));
			} else {
				setModalMsg('메일 전송에 실패했습니다. 잠시 후 다시 시도해 주세요.');
			}
		} catch {
			setModalMsg('메일 전송에 실패했습니다. 잠시 후 다시 시도해 주세요.');
		} finally {
			setSending(false);
		}
	};

	const formatDate = (timestamp) => {
		if (!timestamp) return '';
		const d = new Date(timestamp);
		return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
	};

	// 타임라인 단계 상태
	const step1 = status === 'completed' || status === 'sent' ? 'done' : 'now';
	const step2 = status === 'completed' ? 'done' : status === 'sent' ? 'now' : 'wait';
	const step3 = status === 'completed' ? 'done' : 'wait';
	const dotIcon = (s, n) => (s === 'done' ? '✓' : n);

	return (
		<div>
			<div className="mb-page-header" style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
				<div style={{ flex: 1, minWidth: 220 }}>
					<h2 className="mb-page-title">프리랜서 계약</h2>
					<p className="mb-page-description">보상을 환전하려면 프리랜서 계약이 한 번 필요해요.</p>
				</div>
				{status === 'completed' && <span className="badge badge--mint">계약 완료</span>}
				{status === 'sent' && <span className="badge badge--blue">계약서 발송됨</span>}
			</div>

			{/* 진행 상태 타임라인 */}
			<div className="mb-card">
				<div className="flow">
					<div className={`flow__step ${step1 === 'done' ? 'is-done' : ''}`}>
						<div className="flow__rail">
							<span className={`flow__dot flow__dot--${step1}`}>{dotIcon(step1, 1)}</span>
							<span className="flow__line" />
						</div>
						<div className="flow__body">
							<div className="flow__title">
								계약서 메일 발송
								{step1 === 'done' && <span className="badge badge--mint">완료</span>}
							</div>
							<div className="flow__sub">
								{lastSentAt
									? `${formatDate(lastSentAt)} · 등록된 이메일로 계약서를 보냈어요`
									: '버튼을 누르면 등록된 이메일로 계약서가 발송돼요'}
							</div>
						</div>
					</div>
					<div className={`flow__step ${step2 === 'done' ? 'is-done' : ''}`}>
						<div className="flow__rail">
							<span className={`flow__dot flow__dot--${step2}`}>{dotIcon(step2, 2)}</span>
							<span className="flow__line" />
						</div>
						<div className="flow__body">
							<div className="flow__title">서명 후 회신</div>
							<div className="flow__sub">메일의 계약서에 서명하고 회신해주세요 · 담당자가 영업일 1일 이내로 확인해요</div>
						</div>
					</div>
					<div className={`flow__step ${step3 === 'done' ? 'is-done' : ''}`}>
						<div className="flow__rail">
							<span className={`flow__dot flow__dot--${step3}`}>{dotIcon(step3, 3)}</span>
						</div>
						<div className="flow__body">
							<div className="flow__title">계약 체결 완료</div>
							<div className="flow__sub">
								{status === 'completed'
									? '이제 포인트를 환전할 수 있어요 · 계약자명 변경은 문의하기로 요청해주세요'
									: '체결이 확인되면 포인트를 환전할 수 있어요'}
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="mb-grid-2" style={{ alignItems: 'stretch' }}>
				{/* 왼쪽: 발송/재발송 */}
				<div className="mb-card" style={{ margin: 0, display: 'flex', flexDirection: 'column' }}>
					<h3 className="mb-section-title">{status === 'completed' ? '계약이 완료됐어요' : '계약 전이라면'}</h3>
					<p className="mb-page-description" style={{ lineHeight: 1.7, marginBottom: 14 }}>
						{status === 'completed'
							? '계약이 완료된 상태예요. 포인트 환전이 가능하며, 계약서는 받은 메일에서 다시 확인할 수 있어요.'
							: '버튼을 누르면 등록된 이메일로 계약서가 발송돼요. 서명 후 회신해주시면 담당자 확인 뒤 체결이 완료돼요.'}
					</p>
					{status !== 'completed' && (
						<div style={{ marginTop: 'auto' }}>
							<button
								type="button"
								className="mb-btn mb-btn-primary mb-btn-full"
								onClick={handleSend}
								disabled={sending}
							>
								{sending ? '발송 중...' : status === 'sent' ? '계약서 메일 다시 받기' : '계약서 메일 받기'}
							</button>
							<div className="mb-info-hint" style={{ marginTop: 8, textAlign: 'center' }}>재발송은 5분에 한 번 가능해요.</div>
						</div>
					)}
				</div>

				{/* 오른쪽: 왜 필요한가요 */}
				<div className="mb-card" style={{ margin: 0 }}>
					<h3 className="mb-section-title">왜 필요한가요?</h3>
					<div className="checkline">
						<i>✓</i>
						<div>
							<b>보상 지급의 법적 근거</b>
							<span>환전(현금 지급)에 필요한 프리랜서 위임 계약이에요</span>
						</div>
					</div>
					<div className="checkline">
						<i>✓</i>
						<div>
							<b>세금 처리</b>
							<span>소득세 3.3% 원천징수의 근거가 돼요</span>
						</div>
					</div>
					<div className="checkline">
						<i>✓</i>
						<div>
							<b>한 번만 하면 끝</b>
							<span>최초 1회 체결로 모든 프로젝트에 적용돼요</span>
						</div>
					</div>
				</div>
			</div>

			{modalOpen && (
				<div className="mbm-overlay" onClick={() => setModalOpen(false)}>
					<div className="mb-card mbm" style={{ width: 400, margin: 0, textAlign: 'center', padding: '32px' }} onClick={(e) => e.stopPropagation()}>
						<p style={{ margin: 0, fontSize: 15.5, color: '#0b1324', lineHeight: 1.6 }}>{modalMsg}</p>
						<div className="mbm-foot">
							<button className="mb-btn mb-btn-primary" onClick={() => setModalOpen(false)}>확인</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default InfluencerContractAgree;
